import { GetRowIdParams, GridApi } from "ag-grid-community";
import { AgGrid, toast } from "@jsq/ds/core";
import { type UpdateEntityStatusMutationFn } from "~/graphql/supergraphHooks";
import { type EntityStatus } from "~/graphql/supergraphTypes";
import { ClientDataGraphqlDataSource } from "../ClientDataGraphqlDataSource";

export const getRowId = (params: GetRowIdParams | Partial<GetRowIdParams>): string => {
  if (!params.data || typeof params.data !== "object") return "";
  return (params.data as { entityId?: string }).entityId ?? "";
};

export const updateGridDataSource = (
  gridApi: GridApi | undefined,
  dataSource: ClientDataGraphqlDataSource | null,
): void => {
  if (!gridApi || !dataSource) return;
  gridApi.setGridOption("serverSideDatasource", dataSource);
  gridApi.refreshServerSide({ purge: true });
};

export const handleGridReady = (
  event: AgGrid.GridReadyEvent,
  setGridApi: (api: AgGrid.GridApi) => void,
): void => {
  setGridApi(event.api);
};

export const updateSingleRow = (
  gridApi: GridApi | undefined,
  updatedEntity: {
    id: number;
    name: string;
    arenaId: number;
    status: string;
  },
): void => {
  if (!gridApi) return;

  const rowNode = gridApi.getRowNode(updatedEntity.id.toString());

  if (rowNode && rowNode.data) {
    const currentRowData = rowNode.data;
    const updatedRowData = {
      ...currentRowData,
      entityStatus: updatedEntity.status === "ACTIVE" ? "Active" : "Completed",
      showSpinner: true,
    };

    rowNode.setData(updatedRowData);
  }
};

export const createOnEntityStatusChange = (
  updateEntityStatusMutation: UpdateEntityStatusMutationFn,
  clientId: string,
) => {
  return async (params: AgGrid.NewValueParams) => {
    const newStatus = params.newValue;
    const oldStatus = params.oldValue;
    const entityData = params.data;

    try {
      await updateEntityStatusMutation({
        variables: {
          input: {
            clientId: parseInt(clientId),
            entityId: entityData.entityId,
            status: newStatus.toUpperCase() as EntityStatus,
            data: { old_status: oldStatus, new_status: newStatus },
          },
        },
      });
    } catch {
      toast.error("Unable to update entity status");
      return;
    }
  };
};

export const createIsEntityStatusEditable = (updateLoading: boolean) => {
  return (params: AgGrid.EditableCallbackParams) => {
    const entityData = params.data;
    return !(updateLoading || Boolean(entityData?.showSpinner));
  };
};

export function createHandleUpdateEntityStatusCompleted(
  gridApi: AgGrid.GridApi | undefined,
  setHasActiveEntityUpdates: (value: boolean) => void,
) {
  return (data: {
    updateEntityStatus?: {
      billingRecordCreated: boolean;
      id: number;
      name: string;
      arenaId: number;
      status: string;
    } | null;
  }) => {
    if (data.updateEntityStatus?.billingRecordCreated === false) {
      return toast.error(`Entity status update is already in progress`);
    }

    if (data.updateEntityStatus) {
      updateSingleRow(gridApi, data.updateEntityStatus);
      setHasActiveEntityUpdates(true);
      return toast.success("Entity status updated successfully");
    }
  };
}
